import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Heart, Shield, Users, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Capture referral code from URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('referral_code', refCode.toUpperCase());
      console.log('Referral code captured:', refCode);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        <div className="absolute top-20 -left-20 w-96 h-96 bg-purple-600 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-20 -right-20 w-64 h-64 bg-purple-400 rounded-full blur-3xl opacity-20"></div>
        
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <div className="max-w-md space-y-10">
            <div className="space-y-6">
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="p-5 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-sm">
                  <Heart className="w-14 h-14 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white text-center tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Integrity Home Care LLC
              </h1>
              <p className="text-xl text-purple-200 text-center font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                Staff Portal - Secure Access
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm">
              <p className="text-purple-100 italic text-lg leading-relaxed">
                "Even as the Son of man came not to be ministered unto, but to minister, and to give his life a ransom for many."
              </p>
              <p className="text-purple-300 text-sm mt-4 font-semibold">— Matthew 20:28</p>
            </div>

            <div className="space-y-6 pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-white/10 rounded-xl border border-white/10 mt-0.5">
                  <Shield className="w-5 h-5 text-purple-200" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Secure Access</h3>
                  <p className="text-purple-300 text-sm leading-relaxed">HIPAA-compliant platform protecting your sensitive information</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-white/10 rounded-xl border border-white/10 mt-0.5">
                  <Users className="w-5 h-5 text-purple-200" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Team Collaboration</h3>
                  <p className="text-purple-300 text-sm leading-relaxed">Seamless communication between caregivers and administrators</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-subtle">
        <div className="w-full max-w-md">
          <div className="mb-6">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-100 transition-all duration-200"
              data-testid="back-to-home-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
          
          <div className="card-premium p-8">
            <div className="mb-8 text-center">
              <div className="flex justify-center mb-5">
                <img 
                  src="https://customer-assets.emergentagent.com/job_caregiver-command/artifacts/prj1fr46_Integrity%20Home%20Care%20Logo%20Webpage.png"
                  alt="Integrity Home Care LLC"
                  className="h-20 w-auto"
                />
              </div>
              <h2 className="text-3xl font-bold text-purple-900 mb-2 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Staff Portal
              </h2>
              <p className="text-gray-500 font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                Sign in to access your dashboard
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-purple-900 font-semibold text-sm">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@integrityhomecare.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-12 rounded-xl border-purple-200 focus:border-purple-500 focus:ring-purple-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-purple-900 font-semibold text-sm">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="h-12 rounded-xl border-purple-200 focus:border-purple-500 focus:ring-purple-500"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 btn-primary text-base"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">
                Need help? Contact your administrator
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}